// alterRoutine — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-routine' })` op (DSE2 §3.D7).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI.
//
// Per-engine surface (per DSE2 dialect.routine):
//   - oracle:   ALTER {PROCEDURE|FUNCTION} <name> COMPILE      (recompile only)
//   - postgres: ALTER {PROCEDURE|FUNCTION} <name> RENAME TO <new>  (rename only)
//   - mssql:    unsupported — redefine via op:'create-routine' { mssqlOrAlter: true }.
//   - mysql:    unsupported — drop and recreate.
//   - sqlite:   unsupported — no stored routines.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  RoutineChange,
  RoutineKindShell,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AlterRoutineArgs {
  readonly routineName: QualifiedName;
  readonly routineKind: RoutineKindShell;
  readonly change: RoutineChange;
}

export function buildAlterRoutineEdit(args: AlterRoutineArgs): DdlEdit {
  return {
    op: 'alter-routine',
    routineName: args.routineName,
    routineKind: args.routineKind,
    change: args.change,
  };
}

export async function commitAlterRoutine(
  ctx: VqeDdlEditContext,
  args: AlterRoutineArgs,
): Promise<void> {
  const edit = buildAlterRoutineEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_ROUTINE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'postgres', 'oracle',
] as const);

export function isAlterRoutineSupported(engine: SqlEngine): boolean {
  return ALTER_ROUTINE_SUPPORTED.has(engine);
}

export function getAlterRoutineUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') {
    return 'mssql has no portable ALTER PROCEDURE / FUNCTION shape — '
      + 'redefine via CREATE OR ALTER, or use sp_rename for renames.';
  }
  if (engine === 'mysql') {
    return 'mysql has no ALTER for routine signatures — drop the routine and '
      + 'recreate it.';
  }
  if (engine === 'sqlite') {
    return 'SQLite has no stored routines.';
  }
  return '';
}

export type AlterRoutineActionKind = 'recompile' | 'rename';

/** Per-engine portable action set. oracle accepts recompile (COMPILE);
 *  postgres accepts rename (RENAME TO). Other engines surface unsupported. */
export function getAvailableAlterRoutineActions(
  engine: SqlEngine,
): ReadonlyArray<AlterRoutineActionKind> {
  if (engine === 'oracle') return ['recompile'];
  if (engine === 'postgres') return ['rename'];
  return [];
}

export function validateAlterRoutineForEngine(
  engine: SqlEngine,
  args: AlterRoutineArgs,
): string | null {
  if (!isAlterRoutineSupported(engine)) {
    return getAlterRoutineUnsupportedTooltip(engine);
  }
  if (args.routineName.name.trim().length === 0) {
    return 'Routine name is required.';
  }
  const hasRecompile = args.change.recompile === true;
  const hasRename = args.change.renameTo !== undefined;
  if (!hasRecompile && !hasRename) {
    return `Pick an action: ${engine === 'oracle' ? 'COMPILE' : 'RENAME TO'}.`;
  }
  if (hasRecompile && hasRename) {
    return `Pick recompile OR rename, not both — emit two separate edits.`;
  }
  if (engine === 'oracle' && hasRename) {
    return `Oracle ALTER PROCEDURE / FUNCTION has no RENAME — use Oracle's `
      + `RENAME old TO new statement, or drop and recreate.`;
  }
  if (engine === 'postgres' && hasRecompile) {
    return `PostgreSQL has no explicit recompile — drop and recreate to bust the plan cache.`;
  }
  if (hasRename && (!args.change.renameTo
      || args.change.renameTo.name.trim().length === 0)) {
    return `New routine name is required.`;
  }
  return null;
}
